import { Fragment, useMemo, useRef } from 'react';
import { withStyles, Tooltip, formattedCurrency, ClientAccountBalance } from 'vopay';
import { useTranslation } from 'react-i18next';
import styles from './useTableConfig.module.scss';
import Chip from './Chip/Chip';

const cn = withStyles(styles);

export function Labels({ labels }: { labels: string[] }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const ref = useRef<HTMLDivElement | null>(null);

  const overflowing = useMemo(() => {
    if (!ref.current) return false;

    return ref.current.scrollHeight > ref.current.clientHeight;
  }, [ref.current]);

  if (!labels.length) return <>-</>;

  return (
    <div className={cn('labelsContainer')}>
      <div ref={ref} className={cn('labels')}>
        {[...labels].map((label, idx) => (
          <Chip key={label + idx} label={label} className={cn('chip')} />
        ))}
      </div>

      {overflowing && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('allLabels', { defaultValue: 'All Labels' })}</h3>

              <ul>
                {[...labels].map((label, idx) => (
                  <li key={label + idx}>
                    <Chip label={label} className={cn('label')} />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

export function Limits({
  limit,
  balances,
  country,
}: {
  limit: string;
  balances?: ClientAccountBalance[];
  country: string;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const defaultCurrency = country === 'US' ? 'USD' : 'CAD';

  if (!balances) {
    return (
      <div>
        {defaultCurrency} {formattedCurrency(limit)}
      </div>
    );
  }

  return (
    <div className={cn('limitsCell')}>
      {balances.slice(0, 2).map((b) => (
        <div key={b.Currency || defaultCurrency}>
          {b.Currency || defaultCurrency} {formattedCurrency(limit.toString())}
        </div>
      ))}

      {balances.length > 2 && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('Tooltip.transactionLimits', { defaultValue: 'Transaction Limits' })}</h3>

              <ul>
                {balances.map((b, idx) => (
                  <li key={(b.Currency || defaultCurrency) + idx}>
                    <Chip
                      label={`${b.Currency || defaultCurrency} ${formattedCurrency(limit)}`}
                      className={cn('label')}
                    />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

export function Balances({ balances, country }: { balances?: ClientAccountBalance[]; country: string }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const defaultCurrency = country === 'US' ? 'USD' : 'CAD';

  if (!balances) return <>-</>;

  return (
    <div className={cn('balancesCell')}>
      {balances.slice(0, 2).map((b) => (
        <BalanceBreakdown key={b.Currency || defaultCurrency} balance={b} currency={b.Currency || defaultCurrency} />
      ))}

      {balances.length > 2 && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('balances', { defaultValue: 'Balances' })}</h3>

              <ul>
                {balances.map((b, idx) => (
                  <li key={(b.Currency || defaultCurrency) + idx}>
                    <BalanceBreakdown balance={b} currency={b.Currency || defaultCurrency} />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

// The Balance cell breaks each currency down into Available / Pending / Total, each value under its
// own label and the three separated by a muted slash. Shared inline (first two currencies) and in the
// multi-currency tooltip.
function BalanceBreakdown({ balance, currency }: { balance: ClientAccountBalance; currency: string }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });

  const items = [
    { label: t('Balance.available', { defaultValue: 'Available' }), value: balance.AvailableBalance },
    { label: t('Balance.pending', { defaultValue: 'Pending' }), value: balance.PendingBalance },
    { label: t('Balance.total', { defaultValue: 'Total' }), value: balance.Balance },
  ];

  return (
    <div className={cn('balanceBreakdown')}>
      {items.map((item, idx) => (
        <Fragment key={item.label}>
          {idx > 0 && <span className={cn('balanceSeparator')}>/</span>}

          <div className={cn('balanceItem')}>
            <span className={cn('balanceLabel')}>{item.label}</span>

            <span className={cn('balanceValueRow')}>
              <span className={cn('balanceCurrency')}>{currency}</span>
              <span className={cn('balanceValue')}>{formattedCurrency(String(item.value))}</span>
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}
